import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
  Pressable,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { MarketplaceListing } from '@/types/residents/marketplaceListing';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { useChatAttachments } from '@/hooks/residents/useChatAttachments';
import { useResponsive } from '@/hooks/common/useResponsive';
import { showConfirmWithButtons } from '@/utils/common/alert';
import AttachmentPreview from '@/components/residents/propertyChat/mobile/AttachmentPreview';
import {
  DateSeparator,
  SentMessageBubble,
  ReceivedMessageBubble,
  ChatInputBar,
  ChatHeader,
  groupMessagesByDate,
  useChatStyles,
  useChatColors,
} from './shared';
import { useStartConversationMutation, useSendMessageMutation } from '@/store/api';

const ChatBubbleIcon = RESIDENTS_SVG_COMPONENTS.START_CONVO;

// Quick reply suggestions
const QUICK_REPLIES = [
  'Hi, is this still available?',
  "Hi, I'd like to buy this",
  'Hi, is the price negotiable?',
];

// Message types
interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'seller';
  timestamp: Date;
}

interface SellerChatModalProps {
  visible: boolean;
  onClose: () => void;
  listing: MarketplaceListing;
}

export const SellerChatModal: React.FC<SellerChatModalProps> = ({
  visible,
  onClose,
  listing,
}) => {
  const insets = useSafeAreaInsets();
  const MARKETPLACE_COLORS = useMarketplaceColors();
  const chatStyles = useChatStyles();
  const chatColors = useChatColors();
  const styles = useMemo(() => buildStyles(MARKETPLACE_COLORS), [MARKETPLACE_COLORS]);
  const scrollViewRef = useRef<ScrollView>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [messageText, setMessageText] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);

  // Responsive layout
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';

  // API mutations
  const [startConversation] = useStartConversationMutation();
  const [sendMessage] = useSendMessageMutation();
  
  // Attachment handling (reusing property chat hook)
  const { 
    attachments,
    filesForApi, // Already in API-ready format
    addFromCamera, 
    addFromLibrary, 
    addFromWebFilePicker,
    removeAttachment, 
    resetAttachments,
  } = useChatAttachments();

  const isEmpty = messages.length === 0;

  // Reset conversation state when modal visibility changes
  useEffect(() => {
    if (!visible) {
      // Reset when modal closes
      setMessages([]);
      setConversationId(null);
      setMessageText('');
      resetAttachments();
    }
  }, [visible, resetAttachments]);

  // Auto scroll to bottom when new messages arrive
  useEffect(() => {
    if (messages.length > 0) {
      // Clear any existing timeout to prevent memory leaks
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }
    
    // Cleanup timeout on unmount or dependency change
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [messages]);

  const handleSend = async () => {
    const trimmedMessage = messageText.trim();
    const hasFiles = attachments.length > 0;
    
    // Require either text or files
    if ((!trimmedMessage && !hasFiles) || isSending) return;
    
    setIsSending(true);
    
    // Optimistically add message to UI
    const optimisticMessage: ChatMessage = {
      id: `optimistic-${Date.now()}`,
      text: trimmedMessage || (hasFiles ? `Attached ${attachments.length} file(s)` : ''),
      sender: 'user',
      timestamp: new Date(),
    };
    
    setMessages(prev => [...prev, optimisticMessage]);
    setMessageText('');
    const filesToSend = filesForApi; // Already in API-ready format
    resetAttachments();
    
    try {
      if (!conversationId) {
        // Start a new conversation with this listing
        const result = await startConversation({
          listingId: listing.id,
          message: trimmedMessage || undefined,
          files: filesToSend,
        }).unwrap();
        if (result?.conversationId) {
          setConversationId(result.conversationId);
        }
      } else {
        // Send message to existing conversation
        await sendMessage({
          conversationId,
          text: trimmedMessage || undefined,
          files: filesToSend,
        }).unwrap();
      }
      
      // if (onSendMessage) {
      //   onSendMessage(trimmedMessage);
      // }
    } catch (error) {
      console.error('Failed to send message:', error);
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => m.id !== optimisticMessage.id));
      setMessageText(trimmedMessage); // Restore the message
    } finally {
      setIsSending(false);
    }
  };

  const handleQuickReply = (reply: string) => {
    setMessageText(reply);
  };

  const handleAttach = () => {
    // On web, use the web file picker with image-only MIME type validation
    if (Platform.OS === 'web') {
      addFromWebFilePicker();
      return;
    }
    // On native, show camera/library options
    showConfirmWithButtons(
      'Add Photos',
      'Choose how you want to add photos',
      'Photo Library',
      'Take Photo',
      addFromLibrary,
      addFromCamera
    );
  };

  // Render grouped messages
  const renderMessages = () => {
    const groupedMessages = groupMessagesByDate(messages);

    return groupedMessages.map((group, groupIndex) => (
      <View key={groupIndex} style={chatStyles.messageGroup}>
        <DateSeparator date={group.messages[0].timestamp} />
        <View style={chatStyles.messagesInGroup}>
          {group.messages.map((msg) => (
            msg.sender === 'user' ? (
              <SentMessageBubble
                key={msg.id}
                text={msg.text}
                timestamp={msg.timestamp}
              />
            ) : (
              <ReceivedMessageBubble
                key={msg.id}
                text={msg.text}
                timestamp={msg.timestamp}
                userName={listing.seller.name}
                userInitials={listing.seller.initials}
              />
            )
          ))}
        </View>
      </View>
    ));
  };

  // Modal content shared between desktop and mobile layouts
  const modalContent = (
    <KeyboardAvoidingView
      style={[styles.container, isDesktop && styles.containerDesktop]}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={0}
    >
      {/* Header */}
      <ChatHeader
        title={listing.title}
        subtitle={listing.seller.name}
        imageUri={listing.images[0]?.url}
        onClose={onClose}
      />

      {/* Chat Content */}
      <ScrollView
        ref={scrollViewRef}
        style={chatStyles.chatContent}
        contentContainerStyle={chatStyles.chatContentContainer}
        showsVerticalScrollIndicator={false}
      >
        {isEmpty ? (
          /* Empty State */
          <View style={chatStyles.emptyState}>
            <View style={chatStyles.emptyIconContainer}>
              <ChatBubbleIcon width={77} height={68} color={chatColors.textMuted} />
            </View>
            <Text style={chatStyles.emptyTitle}>Start conversation</Text>
            <Text style={chatStyles.emptySubtitle}>
              Say hi and start the conversation!
            </Text>
          </View>
        ) : (
          /* Messages */
          <View style={chatStyles.messagesContainer}>
            {renderMessages()}
          </View>
        )}
      </ScrollView>

      {/* Quick Replies (only show when empty) */}
      {isEmpty && (
        <View style={chatStyles.quickRepliesContainer}>
          {QUICK_REPLIES.map((reply, index) => (
            <TouchableOpacity
              key={index}
              style={chatStyles.quickReplyButton}
              onPress={() => handleQuickReply(reply)}
              activeOpacity={0.7}
            >
              <Text style={chatStyles.quickReplyText}>{reply}</Text>
            </TouchableOpacity>
          ))}
        </View>
      )}

      {/* Attachment Preview */}
      {attachments.length > 0 && (
        <AttachmentPreview 
          attachments={attachments} 
          onRemove={removeAttachment} 
        />
      )}

      {/* Message Input Bar */}
      <ChatInputBar
        value={messageText}
        onChangeText={setMessageText}
        onSend={handleSend}
        onAttach={handleAttach}
        paddingBottom={isDesktop ? 20 : Math.max(insets.bottom, 20)}
        hasAttachments={attachments.length > 0}
      />
    </KeyboardAvoidingView>
  );

  // Desktop: Render as centered dialog with backdrop
  if (isDesktop) {
    return (
      <Modal
        visible={visible}
        animationType="fade"
        transparent={true}
        onRequestClose={onClose}
      >
        <View style={styles.desktopOverlay}>
          <Pressable style={styles.desktopBackdrop} onPress={onClose} />
          <View style={styles.desktopDialog}>
            {modalContent}
          </View>
        </View>
      </Modal>
    );
  }

  // Mobile/Tablet: Render as fullscreen page sheet
  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      {modalContent}
    </Modal>
  );
};

type MarketplaceColors = ReturnType<typeof useMarketplaceColors>;

const buildStyles = (MARKETPLACE_COLORS: MarketplaceColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
  },
  containerDesktop: {
    borderRadius: 20,
    overflow: 'hidden',
  },
  // Desktop overlay and dialog
  desktopOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  desktopBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  desktopDialog: {
    width: '90%',
    maxWidth: 480,
    height: '80%',
    maxHeight: 640,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 16,
    elevation: 8,
  },
});
